import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

// Helper function to get full image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8000';
  const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  return `${baseUrl}${normalizedPath}`;
};

const CartItem = ({ item, onUpdateQuantity, onRemove }) => {
  const [imageError, setImageError] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const imageUrl = getImageUrl(item.image);
  const itemPrice = item.discount_price || item.price;
  const itemTotal = itemPrice * item.quantity;

  const handleQuantityChange = async (newQuantity) => {
    if (newQuantity < 1) return;
    if (newQuantity > (item.stock || 99)) return;
    
    setLoading(true);
    await onUpdateQuantity(item.id, item.size, item.color, newQuantity);
    setLoading(false);
  };

  const handleRemove = async () => {
    setLoading(true);
    await onRemove(item.id, item.size, item.color);
    setLoading(false);
  };

  return (
    <div className="py-6 border-b border-gray-100 last:border-0">
      <div className="flex gap-5">
        {/* Product Image */}
        <div className="flex-shrink-0 w-20 h-24 bg-gray-50 overflow-hidden">
          {!imageError && imageUrl ? (
            <img
              src={imageUrl}
              alt={item.title}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-[10px] text-gray-400">No image</span>
            </div>
          )}
        </div>

        {/* Product Details */}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-sm font-light text-gray-900 truncate">
                {item.title}
              </h3>
              <p className="text-xs text-gray-400 mt-1">
                Size: {item.size} / Color: {item.color}
              </p>
            </div>
            
            {/* Price */}
            <div className="text-right flex-shrink-0">
              <p className="text-sm font-light text-gray-900 whitespace-nowrap">
                ${itemTotal.toFixed(2)}
              </p>
              {item.discount_price && (
                <p className="text-xs text-gray-400 line-through">
                  ${(item.price * item.quantity).toFixed(2)}
                </p>
              )}
            </div>
          </div>

          {/* Quantity and Remove */}
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center border border-gray-200">
              <button
                onClick={() => handleQuantityChange(item.quantity - 1)}
                disabled={loading || item.quantity <= 1}
                className="w-7 h-7 text-gray-500 hover:text-gray-900 transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                -
              </button>
              <span className="w-8 text-center text-sm text-gray-900">
                {item.quantity}
              </span>
              <button
                onClick={() => handleQuantityChange(item.quantity + 1)}
                disabled={loading || (item.stock && item.quantity >= item.stock)}
                className="w-7 h-7 text-gray-500 hover:text-gray-900 transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                +
              </button>
            </div>
            
            <button
              onClick={handleRemove}
              disabled={loading}
              className="text-gray-400 hover:text-gray-900 transition disabled:opacity-40"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
          
          {/* Stock warning */}
          {item.stock && item.stock <= 5 && item.stock > 0 && (
            <p className="text-[10px] text-gray-400 mt-2">
              Only {item.stock} left
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartItem;